import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Mail, Copy, Check, Save, Building2, Contact as ContactIcon, TrendingUp, FolderKanban, Calendar as CalIcon, ClipboardList, Bell, UserCog, Plus, ChevronDown } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none transition-colors focus:border-primary";

const MEETING_TYPES = [
  ["", "Type…"], ["discovery", "Discovery"], ["demo", "Demo"], ["check_in", "Check-in"],
  ["internal", "Internal"], ["kickoff", "Kickoff"], ["review", "Review"], ["other", "Other"],
];
const MEETING_MODES = [["", "Mode…"], ["in_person", "In person"], ["video", "Video"], ["phone", "Phone"]];

const ACTION_META = {
  save_crm_activity: { icon: UserCog, label: "Save CRM activity", tone: "bg-brand-teal/10 text-brand-teal" },
  update_contact: { icon: ContactIcon, label: "Update contact notes", tone: "bg-brand-teal/10 text-brand-teal" },
  update_deal: { icon: TrendingUp, label: "Update deal", tone: "bg-brand-orange/10 text-brand-orange" },
  create_task: { icon: ClipboardList, label: "Create task", tone: "bg-brand-teal/10 text-brand-teal" },
  create_reminder: { icon: Bell, label: "Create reminder", tone: "bg-brand-teal/10 text-brand-teal" },
  create_calendar_event: { icon: CalIcon, label: "Add calendar event", tone: "bg-brand-teal/10 text-brand-teal" },
  update_project: { icon: FolderKanban, label: "Update project", tone: "bg-brand-orange/10 text-brand-orange" },
  suggest_new_project: { icon: FolderKanban, label: "Create new project", tone: "bg-brand-red/10 text-brand-red" },
};

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text || ""); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      data-testid="meeting-copy-btn"
    >
      {done ? <Check className="h-3.5 w-3.5 text-brand-teal" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : "Copy"}
    </button>
  );
}

function useWorkspaceLookups() {
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [c, ct, d, p] = await Promise.all([
          api.get("/companies"), api.get("/contacts"),
          api.get("/deals"), api.get("/projects"),
        ]);
        setCompanies(c.data.companies || []);
        setContacts(ct.data.contacts || []);
        setDeals(d.data.deals || []);
        setProjects(p.data.projects || []);
      } catch { /* silent */ }
    })();
  }, []);
  return { companies, contacts, deals, projects };
}

export default function MeetingAssist() {
  const { companies, contacts, deals, projects } = useWorkspaceLookups();
  const [notes, setNotes] = useState("");
  const [ctx, setCtx] = useState({
    meeting_type: "", meeting_mode: "", meeting_at: "", participants: "",
    company_id: "", contact_id: "", deal_id: "", project_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // {created:{...}} once applied
  const [meetingId, setMeetingId] = useState(null);
  const [result, setResult] = useState(null); // { summary, follow_up_email, actions: [] }
  const [similarDeals, setSimilarDeals] = useState([]);

  // Filter contacts by chosen company
  const contactsForCompany = ctx.company_id ? contacts.filter((c) => c.company_id === ctx.company_id) : contacts;

  const analyze = async (e) => {
    e.preventDefault();
    if (!notes.trim()) return;
    setLoading(true);
    setResult(null);
    setSaved(null);
    try {
      const payload = { notes, ...ctx };
      // Convert "" -> undefined for optional fields
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (payload.meeting_at) {
        payload.meeting_at = new Date(payload.meeting_at).toISOString();
      }
      const { data } = await api.post("/ai/meeting-assist/analyze", payload);
      setResult(data.result);
      setMeetingId(data.id);
      setSimilarDeals(data.similar_deals || []);
      track("meeting_assist_used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not process notes.");
    } finally { setLoading(false); }
  };

  const toggleAction = (id, val) => {
    setResult((r) => r ? { ...r, actions: (r.actions || []).map((a) => a.id === id ? { ...a, selected: val } : a) } : r);
  };
  const patchAction = (id, patch) => {
    setResult((r) => r ? { ...r, actions: (r.actions || []).map((a) => a.id === id ? { ...a, ...patch } : a) } : r);
  };

  const applyActions = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const selected = (result.actions || []).filter((a) => a.selected);
      const payload = {
        meeting_id: meetingId,
        company_id: ctx.company_id || undefined,
        contact_id: ctx.contact_id || undefined,
        deal_id: ctx.deal_id || undefined,
        project_id: ctx.project_id || undefined,
        actions: selected,
      };
      const { data } = await api.post("/ai/meeting-assist/apply", payload);
      const c = data.created || {};
      const parts = [];
      if (c.tasks) parts.push(`${c.tasks} task${c.tasks === 1 ? "" : "s"}`);
      if (c.reminders) parts.push(`${c.reminders} reminder${c.reminders === 1 ? "" : "s"}`);
      if (c.events) parts.push(`${c.events} event${c.events === 1 ? "" : "s"}`);
      if (c.contact_updates) parts.push("contact updated");
      if (c.deal_updates) parts.push("deal updated");
      if (c.project_updates) parts.push("project updated");
      if (c.projects) parts.push(`${c.projects} new project`);
      if (c.activities) parts.push(`${c.activities} activity logged`);
      toast.success(parts.length ? `Added to workspace: ${parts.join(", ")}` : "Nothing to add");
      setSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply actions.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="meeting-assist-page">
      <Seo title="Meeting Assist" description="Turn meeting notes into follow-ups, tasks and record updates." path="/app/meeting" />
      <div className="mb-6">
        <p className="eyebrow text-brand-teal">Post-Meeting Assist</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Turn a meeting into connected action.</h1>
        <p className="mt-2 text-sm text-muted-foreground">Attach the meeting to real records — your AI proposes tasks, updates and follow-ups you approve before anything is written.</p>
      </div>

      <form onSubmit={analyze} className="rounded-2xl border border-border bg-card p-6" data-testid="meeting-form">
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1"><CalIcon className="h-3 w-3" /> Meeting date/time</span>
            <input type="datetime-local" value={ctx.meeting_at} onChange={(e) => setCtx({ ...ctx, meeting_at: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-when" />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Type
            <select value={ctx.meeting_type} onChange={(e) => setCtx({ ...ctx, meeting_type: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-type">
              {MEETING_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Mode
            <select value={ctx.meeting_mode} onChange={(e) => setCtx({ ...ctx, meeting_mode: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-mode">
              {MEETING_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> Related company</span>
            <select value={ctx.company_id} onChange={(e) => setCtx({ ...ctx, company_id: e.target.value, contact_id: "" })} className={`${inputCls} mt-1`} data-testid="meeting-company">
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1"><ContactIcon className="h-3 w-3" /> Related contact</span>
            <select value={ctx.contact_id} onChange={(e) => setCtx({ ...ctx, contact_id: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-contact">
              <option value="">—</option>
              {contactsForCompany.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Related deal</span>
            <select value={ctx.deal_id} onChange={(e) => setCtx({ ...ctx, deal_id: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-deal">
              <option value="">—</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.title}{d.stage ? ` · ${d.stage}` : ""}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1"><FolderKanban className="h-3 w-3" /> Related project</span>
            <select value={ctx.project_id} onChange={(e) => setCtx({ ...ctx, project_id: e.target.value })} className={`${inputCls} mt-1`} data-testid="meeting-project">
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground sm:col-span-2">
            Participants (optional)
            <input value={ctx.participants} onChange={(e) => setCtx({ ...ctx, participants: e.target.value })} className={`${inputCls} mt-1`} placeholder="Jane (Acme CTO), Bob" data-testid="meeting-participants" />
          </label>
        </div>
        <label className="mb-1.5 block text-sm font-medium">Meeting notes</label>
        <textarea required rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputCls} resize-none`} data-testid="meeting-notes-input" placeholder="Paste raw notes from your call or meeting…" />
        <button type="submit" disabled={loading} data-testid="meeting-generate-btn" className="group mt-4 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60">
          <Sparkles className="h-4 w-4" /> {loading ? "Analysing…" : "Analyse with AI"}
        </button>
      </form>

      {result && (
        <>
          <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="meeting-result">
            <Card icon={Sparkles} title="Summary">
              <p className="text-sm leading-relaxed text-muted-foreground">{result.summary}</p>
            </Card>
            <Card icon={Mail} title="Follow-up email" action={<CopyBtn text={result.follow_up_email} />}>
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">{result.follow_up_email}</pre>
            </Card>
          </div>

          {similarDeals.length > 0 && !ctx.deal_id && (
            <div className="mt-4 rounded-2xl border border-brand-teal/40 bg-brand-teal/5 p-4" data-testid="meeting-similar-deals">
              <p className="text-xs font-semibold text-brand-teal">Existing deals on this company — link one instead of creating new?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {similarDeals.map((d) => (
                  <button key={d.id} onClick={() => setCtx({ ...ctx, deal_id: d.id })} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium hover:border-brand-teal" data-testid={`link-deal-${d.id}`}>
                    Link "{d.title}" · {d.stage}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="action-preview">
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <div>
                <p className="font-display text-base font-semibold tracking-tight">AI action preview</p>
                <p className="mt-1 text-xs text-muted-foreground">Untick anything you don't want. All connected records stay linked.</p>
              </div>
              <p className="text-xs text-muted-foreground">{(result.actions || []).filter((a) => a.selected).length} of {(result.actions || []).length} selected</p>
            </div>
            {(!result.actions || result.actions.length === 0) ? (
              <p className="text-sm text-muted-foreground">No actions proposed for this meeting.</p>
            ) : (
              <div className="space-y-2" data-testid="action-list">
                {result.actions.map((a) => (
                  <ActionRow key={a.id} action={a} onToggle={(v) => toggleAction(a.id, v)} onPatch={(p) => patchAction(a.id, p)} />
                ))}
              </div>
            )}
            <div className="mt-5 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Records created will be linked to
                {ctx.company_id && <> the selected company,</>}
                {ctx.contact_id && <> the selected contact,</>}
                {ctx.deal_id && <> the selected deal,</>}
                {ctx.project_id && <> the selected project.</>}
                {!ctx.company_id && !ctx.contact_id && !ctx.deal_id && !ctx.project_id && " nothing — link records above to preserve relationships."}
              </p>
              <button onClick={applyActions} disabled={saving || saved} data-testid="apply-actions-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {saved ? "Added to workspace" : saving ? "Saving…" : "Add to workspace"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ActionRow({ action, onToggle, onPatch }) {
  const meta = ACTION_META[action.type] || { icon: ClipboardList, label: action.type, tone: "bg-muted text-muted-foreground" };
  const Icon = meta.icon;
  const [open, setOpen] = useState(false);
  const editable = ["create_task", "create_reminder", "create_calendar_event", "update_deal", "update_contact", "update_project", "suggest_new_project"].includes(action.type);
  return (
    <div className={`rounded-xl border p-3 ${action.selected ? "border-brand-teal/40 bg-brand-teal/5" : "border-border bg-background"}`} data-testid={`action-${action.id}`}>
      <div className="flex items-start gap-3">
        <label className="inline-flex items-center pt-1">
          <input type="checkbox" checked={action.selected !== false} onChange={(e) => onToggle(e.target.checked)} data-testid={`action-toggle-${action.id}`} className="h-4 w-4 rounded border-border" />
        </label>
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.tone}`}><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{meta.label}</p>
          <p className="text-xs text-muted-foreground">{action.label || action.title || action.name || "—"}</p>
          {action.type === "suggest_new_project" && action.reason && <p className="mt-1 text-[11px] italic text-brand-red">Why new: {action.reason}</p>}
        </div>
        {editable && (
          <button onClick={() => setOpen((o) => !o)} className="text-muted-foreground hover:text-foreground" data-testid={`action-expand-${action.id}`}>
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && editable && (
        <div className="mt-3 grid gap-2 border-t border-border pt-3 sm:grid-cols-2" data-testid={`action-edit-${action.id}`}>
          {action.title !== undefined && (
            <label className="text-xs text-muted-foreground">Title
              <input value={action.title || ""} onChange={(e) => onPatch({ title: e.target.value })} className={`${inputCls} mt-1`} />
            </label>
          )}
          {action.name !== undefined && (
            <label className="text-xs text-muted-foreground">Project name
              <input value={action.name || ""} onChange={(e) => onPatch({ name: e.target.value })} className={`${inputCls} mt-1`} />
            </label>
          )}
          {(action.due !== undefined || action.next_follow_up !== undefined) && (
            <label className="text-xs text-muted-foreground">Due / follow-up date
              <input type="date" value={action.due || action.next_follow_up || ""} onChange={(e) => onPatch(action.type === "update_deal" ? { next_follow_up: e.target.value } : { due: e.target.value })} className={`${inputCls} mt-1`} />
            </label>
          )}
          {action.starts_at !== undefined && (
            <label className="text-xs text-muted-foreground">Event date/time
              <input type="datetime-local" value={action.starts_at || ""} onChange={(e) => onPatch({ starts_at: e.target.value })} className={`${inputCls} mt-1`} />
            </label>
          )}
          {action.priority !== undefined && (
            <label className="text-xs text-muted-foreground">Priority
              <select value={action.priority} onChange={(e) => onPatch({ priority: e.target.value })} className={`${inputCls} mt-1`}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </label>
          )}
          {action.stage !== undefined && (
            <label className="text-xs text-muted-foreground">Deal stage
              <select value={action.stage} onChange={(e) => onPatch({ stage: e.target.value })} className={`${inputCls} mt-1`}>
                {["new","qualified","proposal","negotiation","won","lost"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}
          {action.notes !== undefined && (
            <label className="text-xs text-muted-foreground sm:col-span-2">Notes
              <textarea rows={2} value={action.notes || ""} onChange={(e) => onPatch({ notes: e.target.value })} className={`${inputCls} mt-1 resize-none`} />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children, action, className = "" }) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted/60 text-brand-teal"><Icon className="h-4 w-4" /></span>
          <h3 className="font-display text-sm font-semibold tracking-tight">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
